import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Building2, Settings, Shield, ArrowLeft, User, Bell, Users, CreditCard, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserMenu } from '@/components/auth/UserMenu';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAuth } from '@/components/auth/AuthProvider';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { TeamLogo } from '@/components/ui/team-logo';

// Custom Businesses icon
const BusinessesIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M10.3332 2H5.6665C4.56193 2 3.6665 2.89543 3.6665 4V11.3333C3.6665 12.4379 4.56193 13.3333 5.6665 13.3333H10.3332C11.4377 13.3333 12.3332 12.4379 12.3332 11.3333V4C12.3332 2.89543 11.4377 2 10.3332 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12.3335 4H13.6668C14.2191 4 14.6668 4.44772 14.6668 5V11C14.6668 11.5523 14.2191 12 13.6668 12H12.3335" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.66683 4H2.3335C1.78121 4 1.3335 4.44772 1.3335 5V11C1.3335 11.5523 1.78121 12 2.3335 12H3.66683" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 5H6M10 7.66667H6M10 10.3333L6 10.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const navigationItems = [
  {
    title: 'Businesses',
    url: '/',
    icon: BusinessesIcon,
    exact: true
  },
  {
    title: 'Settings',
    url: '/settings',
    icon: Settings,
    exact: false
  },
  {
    title: 'Admin',
    url: '/admin',
    icon: Shield,
    exact: false,
    adminOnly: true
  }
];

const settingsNavigationItems = [
  {
    title: 'Profile',
    url: '/settings/profile',
    icon: User,
    section: 'Account Settings'
  },
  {
    title: 'Appearance',
    url: '/settings/appearance',
    icon: Palette,
    section: 'Account Settings'
  },
  {
    title: 'Notifications',
    url: '/settings/notifications',
    icon: Bell,
    section: 'Account Settings'
  },
  {
    title: 'General',
    url: '/settings/team',
    icon: Building2,
    section: 'Team Settings'
  },
  {
    title: 'Members',
    url: '/settings/members',
    icon: Users,
    section: 'Team Settings'
  },
  {
    title: 'Billing',
    url: '/settings/billing',
    icon: CreditCard,
    section: 'Team Settings'
  }
];

export const AppSidebar = () => {
  const [hasTeamLogo, setHasTeamLogo] = useState(false);
  const { isCollapsed, toggleCollapsed } = useSidebar();
  const { teams, currentTeamId, getUserRole } = useAuth();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = currentTeamId && getUserRole(currentTeamId) === 'admin';
  const isOnSettingsPage = location.pathname.startsWith('/settings');

  const filteredItems = navigationItems.filter(item => 
    !item.adminOnly || isAdmin
  );

  const isActive = (url: string, exact: boolean) => {
    if (exact) {
      return location.pathname === url;
    }
    return location.pathname.startsWith(url);
  };

  const getActiveSettingsSection = () => {
    const path = location.pathname;
    if (path === '/settings' || path === '/settings/') return '/settings/profile';
    return path;
  };

  const groupedSettingsItems = settingsNavigationItems.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {} as Record<string, typeof settingsNavigationItems>);

  if (isMobile) {
    return null; // Mobile sidebar is handled by BreadcrumbBar
  }

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-sidebar-background border-r border-sidebar-border transition-all duration-300 ease-in-out",
        isCollapsed ? "w-14" : "w-64"
      )}
    >
      {/* Sidebar Header */}
      <div className="h-[52px] flex items-center">
        <div className="p-2 w-full border-b border-sidebar-border h-[52px] flex items-center">
          <div className="flex items-center justify-between w-full">
            {isOnSettingsPage ? (
              // Settings Header
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/')}
                  className="p-2 rounded-lg hover:bg-sidebar-accent transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 text-sidebar-foreground" />
                </button>
                {!isCollapsed && <h2 className="text-lg font-semibold text-sidebar-foreground">Settings</h2>}
              </div>
            ) : (
              // Logo Section
              <div
                className={cn(
                  "flex items-center gap-2 p-2 rounded-md transition-all duration-200",
                  isCollapsed ? "justify-center" : ""
                )}
              >
                {/* Logo Mark */}
                <TeamLogo onTeamLogoLoaded={setHasTeamLogo} />

                {/* SMB Text - only show if no team logo is available */}
                {!isCollapsed && !hasTeamLogo && (
                  <span className="font-semibold text-sm text-sidebar-foreground">SMB</span>
                )}
              </div>
            )}

            {/* Right side with UserMenu - always on the right */}
            {!isCollapsed && !isOnSettingsPage && (
              <div className="flex items-center">
                <UserMenu />
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Navigation */}
      <nav className="p-2 space-y-1 flex-1 overflow-y-auto">
        {isOnSettingsPage ? (
          // Settings Navigation
          <div className="space-y-4">
            {Object.entries(groupedSettingsItems).map(([section, items]) => (
              <div key={section}>
                {!isCollapsed && (
                  <h3 className="text-sm font-medium text-muted-foreground mb-3 px-2">{section}</h3>
                )}
                <div className="space-y-1">
                  {items.map((item) => {
                    const Icon = item.icon;
                    const active = getActiveSettingsSection() === item.url;
                    
                    return (
                      <NavLink
                        key={item.url}
                        to={item.url}
                        className={cn(
                          "flex items-center gap-2 px-2 rounded-md text-sm transition-colors group border",
                          "h-8", // 32px tall
                          "hover:bg-sidebar-accent",
                          "focus:outline-none focus-visible:outline-none",
                          active
                            ? "bg-sidebar-accent border-sidebar-border font-normal"
                            : "text-sidebar-foreground font-normal border-transparent",
                          isCollapsed ? "justify-center" : ""
                        )}
                      >
                        <Icon className={cn(
                          "h-4 w-4 flex-shrink-0 transition-colors",
                          active ? "text-sidebar-accent-foreground" : "text-sidebar-foreground/70",
                          "group-hover:text-sidebar-accent-foreground"
                        )} />
                        {!isCollapsed && <span className={cn(
                          "text-sm leading-[14px] transition-all",
                          active ? "text-sidebar-accent-foreground font-normal" : "text-sidebar-foreground font-normal",
                          "group-hover:text-sidebar-accent-foreground"
                        )}>{item.title}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Main Navigation
          <div className="space-y-1">
            {filteredItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.url, item.exact);
              
              return (
                <NavLink
                  key={item.url}
                  to={item.url}
                  className={({ isActive }) => cn(
                    "flex items-center gap-2 px-2 rounded-md text-sm transition-colors group border",
                    "h-8", // 32px tall
                    "hover:bg-sidebar-accent",
                    "focus:outline-none focus-visible:outline-none",
                    isActive
                      ? "bg-sidebar-accent border-sidebar-border font-normal"
                      : "text-sidebar-foreground font-normal border-transparent",
                    isCollapsed ? "justify-center" : ""
                  )}
                >
                  <Icon className={cn(
                    "h-4 w-4 flex-shrink-0 transition-colors",
                    active ? "text-sidebar-accent-foreground" : "text-sidebar-foreground/70",
                    "group-hover:text-sidebar-accent-foreground"
                  )} />
                  {!isCollapsed && <span className={cn(
                    "text-sm leading-[14px] transition-all",
                    active ? "text-sidebar-accent-foreground font-normal" : "text-sidebar-foreground font-normal",
                    "group-hover:text-sidebar-accent-foreground"
                  )}>{item.title}</span>}
                </NavLink>
              );
            })}
          </div>
        )}
      </nav>
    </aside>
  );
};